import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Calendar, ChevronDown, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import axiosInstance from '../../../axiosConfig';

const AVATAR_COLORS = ['bg-brand-blue', 'bg-teal-600', 'bg-amber-600'];

const getInitials = (name) => {
  if (!name) return '?';
  return name.trim().split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
};

const getRoleBadgeClass = (role) => {
  switch (role) {
    case 'Administrator': return 'bg-slate-800 text-white';
    case 'Merchant Admin': return 'bg-teal-50 text-teal-700';
    case 'Entity User': return 'bg-amber-50 text-amber-700';
    case 'Void User': return 'bg-slate-100 text-slate-600';
    default: return 'bg-slate-100 text-slate-600';
  }
};

export default function UserManagementInterface() {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State management
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    search: '',
    selectedRole: 'All Roles',
    selectedMerchant: 'Select merchant',
    accountStatus: 'All',
    startDate: '',
    endDate: ''
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [totalUsers, setTotalUsers] = useState(0);
  const [error, setError] = useState(null);

  // Available page size options
  const pageSizeOptions = [10, 25, 50, 100];

  // Debounced filter values
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  // Initialize pagination and filters from URL params
  useEffect(() => {
    const startFromUrl = parseInt(searchParams.get('start'), 10);
    const lengthFromUrl = parseInt(searchParams.get('length'), 10);
    const searchFromUrl = searchParams.get('search') || '';
    const roleFromUrl = searchParams.get('role') || 'All Roles';
    const merchantFromUrl = searchParams.get('merchant') || 'Select merchant';
    const statusFromUrl = searchParams.get('status') || 'All';
    const startDateFromUrl = searchParams.get('startDate') || '';
    const endDateFromUrl = searchParams.get('endDate') || '';
    
    // Calculate current page from start parameter
    const length = pageSizeOptions.includes(lengthFromUrl) ? lengthFromUrl : 10;
    const start = startFromUrl >= 0 ? startFromUrl : 0;
    const page = Math.floor(start / length) + 1;
    
    setCurrentPage(page);
    setRowsPerPage(length);
    const newFilters = {
      search: searchFromUrl,
      selectedRole: roleFromUrl,
      selectedMerchant: merchantFromUrl,
      accountStatus: statusFromUrl,
      startDate: startDateFromUrl,
      endDate: endDateFromUrl
    };
    setFilters(newFilters);
    setDebouncedFilters(newFilters);
  }, [searchParams]);

  // Debounce text input filters
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [filters]);

  // Update URL parameters
  const updateUrlParams = useCallback((page, size, currentFilters = debouncedFilters) => {
    const params = new URLSearchParams();
    const start = Math.max(0, (page - 1) * size);
    
    params.set('start', start.toString());
    params.set('length', size.toString());
    
    // Add filters to URL if they have values
    if (currentFilters.search) params.set('search', currentFilters.search);
    if (currentFilters.selectedRole && currentFilters.selectedRole !== 'All Roles') 
      params.set('role', currentFilters.selectedRole);
    if (currentFilters.selectedMerchant && currentFilters.selectedMerchant !== 'Select merchant') 
      params.set('merchant', currentFilters.selectedMerchant);
    if (currentFilters.accountStatus && currentFilters.accountStatus !== 'All') 
      params.set('status', currentFilters.accountStatus);
    if (currentFilters.startDate) params.set('startDate', currentFilters.startDate);
    if (currentFilters.endDate) params.set('endDate', currentFilters.endDate);
    
    setSearchParams(params, { replace: true });
  }, [setSearchParams, debouncedFilters]);

  // Handle page change
  const handlePageChange = useCallback((page) => {
    const totalPages = Math.ceil(totalUsers / rowsPerPage) || 1;
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      updateUrlParams(page, rowsPerPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [totalUsers, rowsPerPage, updateUrlParams]);

  // Handle page size change
  const handlePageSizeChange = useCallback((size) => {
    if (pageSizeOptions.includes(size)) {
      setCurrentPage(1); // Reset to first page
      setRowsPerPage(size);
      updateUrlParams(1, size);
    }
  }, [updateUrlParams]);

  // Handle filter changes
  const handleFilterChange = useCallback((filterName, value) => {
    const newFilters = { ...filters, [filterName]: value };
    setFilters(newFilters);
    
    // Auto-apply filters for dropdown selections (not text inputs or dates)
    if (filterName !== 'search' && filterName !== 'startDate' && filterName !== 'endDate') {
      setCurrentPage(1);
      setDebouncedFilters(newFilters);
      updateUrlParams(1, rowsPerPage, newFilters);
    }
  }, [filters, rowsPerPage, updateUrlParams]);

  // Apply filters manually
  const applyFilters = useCallback(() => {
    setCurrentPage(1);
    setDebouncedFilters(filters);
    updateUrlParams(1, rowsPerPage, filters);
  }, [filters, rowsPerPage, updateUrlParams]);

  // Reset filters
  const clearFilters = useCallback(() => {
    const emptyFilters = {
      search: '',
      selectedRole: 'All Roles',
      selectedMerchant: 'Select merchant',
      accountStatus: 'All',
      startDate: '',
      endDate: ''
    };
    setFilters(emptyFilters);
    setDebouncedFilters(emptyFilters);
    setCurrentPage(1);
    updateUrlParams(1, rowsPerPage, emptyFilters);
  }, [rowsPerPage, updateUrlParams]);

  // Fetch user list
  useEffect(() => {
    const fetchUserList = async () => {
      if (currentPage < 1 || rowsPerPage < 1) return;

      try {
        setFilterLoading(true);
        setError(null);
        
        const start = (currentPage - 1) * rowsPerPage;
        const params = {
          start: start,
          length: rowsPerPage,
        };

        // Add filters to params if they have values
        if (debouncedFilters.search) params.search = debouncedFilters.search;
        if (debouncedFilters.selectedRole && debouncedFilters.selectedRole !== 'All Roles') 
          params.role = debouncedFilters.selectedRole;
        if (debouncedFilters.selectedMerchant && debouncedFilters.selectedMerchant !== 'Select merchant') 
          params.merchant = debouncedFilters.selectedMerchant;
        if (debouncedFilters.accountStatus && debouncedFilters.accountStatus !== 'All') 
          params.status = debouncedFilters.accountStatus;
        if (debouncedFilters.startDate) params.startDate = debouncedFilters.startDate;
        if (debouncedFilters.endDate) params.endDate = debouncedFilters.endDate;

        const response = await axiosInstance.get(
          '/internal-api/v1/client-web/user-config/user-list',
          { params }
        );

        if (response.data?.data) {
          setUsers(response.data.data);
          setTotalUsers(response.data.recordsFiltered || 0);
        } else {
          setUsers([]);
          setTotalUsers(0);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        setError("Failed to load users. Please try again.");
        setUsers([]);
        setTotalUsers(0);
      } finally {
        setFilterLoading(false);
        setLoading(false);
      }
    };

    fetchUserList();
  }, [currentPage, rowsPerPage, debouncedFilters]);

  const totalPages = Math.ceil(totalUsers / rowsPerPage) || 1;

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);

      if (startPage > 2) {
        pages.push('...');
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      if (endPage < totalPages - 1) {
        pages.push('...');
      }

      pages.push(totalPages);
    }
    return pages;
  };

  if (error) {
    return (
      <div className="flex items-center justify-center flex-1">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center flex-1">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 border-b-2 rounded-full animate-spin border-brand-blue"></div>
          <div className="text-slate-500">Loading users...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">User Management</h1>
          <div className="mt-1 text-sm text-slate-500">
            Home <span className="mx-1.5 text-slate-400">/</span>
            <span className="font-semibold text-slate-700">Users</span>
          </div>
        </div>
        <a
          href='/UserCreationForm'
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white transition-colors rounded-lg shadow-sm bg-brand-blue hover:bg-brand-blue-dark"
        >
          + Add New User
        </a>
      </div>

      {/* Filter Section */}
      <div className="p-6 bg-white border rounded-xl border-slate-200">
        <h3 className="mb-4 text-sm font-bold text-slate-900">Filter Users</h3>

        <div className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-2 lg:grid-cols-3">
            {/* Search */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-700">
                Search
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by name, email or username..."
                  className="w-full py-2.5 pl-10 pr-4 text-sm bg-white border rounded-lg border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
                <Search className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2 text-slate-400" />
              </div>
            </div>

            {/* Last Login Date */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-700">
                Last Login Date
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="Start Date"
                    className="w-full py-2.5 pl-9 pr-4 text-sm bg-white border rounded-lg border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  />
                  <Calendar className="absolute w-4 h-4 -translate-y-1/2 left-2.5 top-1/2 text-slate-400" />
                </div>
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="End Date"
                    className="w-full py-2.5 pl-9 pr-4 text-sm bg-white border rounded-lg border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  />
                  <Calendar className="absolute w-4 h-4 -translate-y-1/2 left-2.5 top-1/2 text-slate-400" />
                </div>
              </div>
            </div>

            {/* Merchant */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-700">
                Merchant
              </label>
              <div className="relative">
                <select
                  className="w-full py-2.5 pl-4 pr-10 text-sm bg-white border rounded-lg appearance-none cursor-pointer border-slate-300 text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                  value={filters.selectedMerchant}
                  onChange={(e) => handleFilterChange('selectedMerchant', e.target.value)}
                >
                  <option>Select merchant</option>
                  <option>SL Travel</option>
                  <option>Cultural Triangle Authority</option>
                </select>
                <ChevronDown className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none right-3 top-1/2 text-slate-400" />
              </div>
            </div>

            {/* User Role */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-700">
                User Role
              </label>
              <div className="relative">
                <select
                  className="w-full py-2.5 pl-4 pr-10 text-sm bg-white border rounded-lg appearance-none cursor-pointer border-slate-300 text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                  value={filters.selectedRole}
                  onChange={(e) => handleFilterChange('selectedRole', e.target.value)}
                >
                  <option>All Roles</option>
                  <option>Administrator</option>
                  <option>Merchant Admin</option>
                  <option>Void User</option>
                  <option>Entity User</option>
                </select>
                <ChevronDown className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none right-3 top-1/2 text-slate-400" />
              </div>
            </div>

            {/* Account Status */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-700">
                Account Status
              </label>
              <div className="relative">
                <select
                  className="w-full py-2.5 pl-4 pr-10 text-sm bg-white border rounded-lg appearance-none cursor-pointer border-slate-300 text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                  value={filters.accountStatus}
                  onChange={(e) => handleFilterChange('accountStatus', e.target.value)}
                >
                  <option value="All">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Suspended">Suspended</option>
                </select>
                <ChevronDown className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none right-3 top-1/2 text-slate-400" />
              </div>
            </div>
        </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={clearFilters}
              disabled={filterLoading}
              className="px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Clear Filters
            </button>
            <button
              onClick={applyFilters}
              disabled={filterLoading}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white transition-colors rounded-lg bg-brand-blue hover:bg-brand-blue-dark disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {filterLoading ? (
                <>
                  <div className="w-3 h-3 border-b-2 border-white rounded-full animate-spin"></div>
                  <span>Filtering...</span>
                </>
              ) : (
                <span>Apply Filters</span>
              )}
            </button>
            <button
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
            >
              <Download size={16} />
              Export
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-hidden bg-white border rounded-xl border-slate-200">
          <div className="relative overflow-x-auto">
            {filterLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-75">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-b-2 rounded-full animate-spin border-brand-blue"></div>
                  <div className="text-sm text-slate-500">Filtering...</div>
                </div>
              </div>
            )}
            <table className="w-full">
              <thead className="border-b bg-slate-50 border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">
                    ID
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">
                    User
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">
                    Email
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">
                    User Role
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">
                    Last Login
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">
                    Created At
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">
                    Status
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-sm text-center text-slate-400">
                      No users found.
                    </td>
                  </tr>
                ) : (
                  users.map((user, index) => (
                    <tr key={user.id} className="transition-colors hover:bg-slate-50">
                      <td className="px-6 py-4 text-sm whitespace-nowrap text-slate-700">
                        {user.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-xs text-white flex-shrink-0 ${AVATAR_COLORS[index % AVATAR_COLORS.length]}`}>
                            {getInitials(user.username)}
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-slate-900">{user.username}</div>
                            <div className="text-xs text-slate-500">{user.uuid}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap text-slate-600">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${getRoleBadgeClass(user.role)}`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap text-slate-600">
                        {user.lastLoginAt || <span className="text-slate-400">Never</span>}
                      </td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap text-slate-600">
                        {user.createdAt}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                          user.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                        }`}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap">
                        <button className="font-semibold text-brand-blue hover:text-brand-blue-dark">
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-6 py-3 bg-white border-t border-slate-200">
            <div className="text-xs text-slate-500">
              Showing {users.length > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0} to {Math.min(currentPage * rowsPerPage, totalUsers)} of {totalUsers} users
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || filterLoading}
                  className={`p-1 rounded transition-colors ${
                    currentPage === 1 || filterLoading ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <ChevronLeft size={18} />
                </button>

                {getPageNumbers().map((page, index) => (
                  <button
                    key={index}
                    onClick={() => typeof page === 'number' ? handlePageChange(page) : null}
                    disabled={totalUsers === 0 || filterLoading}
                    className={`min-w-8 h-8 px-2 rounded-md text-xs font-semibold transition-colors ${
                      page === currentPage
                        ? 'bg-brand-blue text-white'
                        : page === '...'
                        ? 'cursor-default text-slate-400'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {page}
                  </button>
                ))}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || totalUsers === 0 || filterLoading}
                  className={`p-1 rounded transition-colors ${
                    currentPage === totalPages || totalUsers === 0 || filterLoading
                      ? 'text-slate-300 cursor-not-allowed'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <ChevronRight size={18} />
                </button>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Show</span>
                <div className="relative">
                  <select
                    className="py-1 pl-3 pr-8 text-xs bg-white border rounded-md appearance-none cursor-pointer border-slate-300 focus:outline-none focus:border-brand-blue"
                    value={rowsPerPage}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  >
                    <option>10</option>
                    <option>25</option>
                    <option>50</option>
                    <option>100</option>
                  </select>
                  <ChevronDown className="absolute w-3.5 h-3.5 -translate-y-1/2 pointer-events-none right-2 top-1/2 text-slate-400" />
                </div>
                <span className="text-xs text-slate-500">per page</span>
              </div>
            </div>
          </div>
        </div>
    </div>
  );
}